import React from 'react';
import { FileText, Plus, Upload, Search, AlertTriangle, Shield, BarChart3, GitCompare } from 'lucide-react';

export interface EmptyStateProps {
  type?: 'no-contracts' | 'no-results' | 'error' | 'no-gaps' | 'no-risks' | 'run-analysis' | 'no-comparison';
  title?: string;
  description?: string;
  onUpload?: () => void;
  onClearFilters?: () => void;
  onRunAnalysis?: () => void;
  onViewContracts?: () => void;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  type = 'no-contracts',
  title,
  description,
  onUpload,
  onClearFilters,
  onRunAnalysis,
  onViewContracts,
}) => {
  const getContent = () => {
    switch (type) {
      case 'no-results':
        return {
          icon: Search,
          defaultTitle: 'No contracts found',
          defaultDescription:
            'No contracts match your current filters. Try adjusting your search criteria.',
          showClearFilters: true,
          showUpload: false,
          showRunAnalysis: false,
          showViewContracts: false,
        };
      case 'error':
        return {
          icon: FileText,
          defaultTitle: 'Unable to load contracts',
          defaultDescription:
            'There was an error loading the contracts list. Please try again later.',
          showClearFilters: false,
          showUpload: false,
          showRunAnalysis: false,
          showViewContracts: false,
        };
      case 'no-gaps':
        return {
          icon: AlertTriangle,
          defaultTitle: 'No gaps identified',
          defaultDescription:
            'No compliance gaps were found in the analyzed contracts. Run a comparison analysis to identify potential gaps.',
          showClearFilters: false,
          showUpload: false,
          showRunAnalysis: true,
          showViewContracts: false,
        };
      case 'no-risks':
        return {
          icon: Shield,
          defaultTitle: 'No risks identified',
          defaultDescription:
            'No compliance risks have been analyzed yet. Run a risk analysis on your contracts to identify potential issues.',
          showClearFilters: false,
          showUpload: false,
          showRunAnalysis: true,
          showViewContracts: false,
        };
      case 'run-analysis':
        return {
          icon: BarChart3,
          defaultTitle: 'Ready for analysis',
          defaultDescription:
            'Upload contracts and run analysis to identify compliance gaps and risks across your Medicaid contracts.',
          showClearFilters: false,
          showUpload: true,
          showRunAnalysis: false,
          showViewContracts: false,
        };
      case 'no-comparison':
        return {
          icon: GitCompare,
          defaultTitle: 'No contracts to compare',
          defaultDescription:
            'Upload at least two contracts to run a comparison analysis and identify compliance gaps.',
          showClearFilters: false,
          showUpload: true,
          showViewContracts: true,
          showRunAnalysis: false,
        };
      default:
        return {
          icon: FileText,
          defaultTitle: 'No contracts uploaded yet',
          defaultDescription:
            'Get started by uploading your first Medicaid contract document to analyze.',
          showClearFilters: false,
          showUpload: true,
          showRunAnalysis: false,
          showViewContracts: false,
        };
    }
  };

  const content = getContent();
  const Icon = content.icon;

  return (
    <div
      className="bg-white rounded-lg shadow-md p-12 text-center"
      data-testid={`empty-state-${type}`}
    >
      <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6">
        <Icon className="h-8 w-8 text-gray-400" />
      </div>

      <h3
        className="text-lg font-medium text-gray-900 mb-2"
        data-testid="empty-state-title"
      >
        {title || content.defaultTitle}
      </h3>

      <p
        className="text-gray-500 mb-6 max-w-md mx-auto"
        data-testid="empty-state-description"
      >
        {description || content.defaultDescription}
      </p>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        {content.showUpload && onUpload && (
          <button
            onClick={onUpload}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            data-testid="empty-state-upload-button"
          >
            <Plus className="h-5 w-5 mr-2" />
            Upload Contract
          </button>
        )}

        {content.showRunAnalysis && onRunAnalysis && (
          <button
            onClick={onRunAnalysis}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            data-testid="empty-state-run-analysis-button"
          >
            <BarChart3 className="h-5 w-5 mr-2" />
            Run Analysis
          </button>
        )}

        {content.showViewContracts && onViewContracts && (
          <button
            onClick={onViewContracts}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            data-testid="empty-state-view-contracts-button"
          >
            <FileText className="h-5 w-5 mr-2" />
            View Contracts
          </button>
        )}

        {content.showClearFilters && onClearFilters && (
          <button
            onClick={onClearFilters}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            data-testid="empty-state-clear-filters"
          >
            Clear Filters
          </button>
        )}
      </div>

      {(type === 'no-contracts' || type === 'no-comparison' || type === 'run-analysis') && (
        <div className="mt-8 pt-8 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-4">
            Supported file formats
          </h4>
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center text-sm text-gray-500">
              <Upload className="h-4 w-4 mr-1 text-gray-400" />
              PDF
            </div>
            <div className="flex items-center text-sm text-gray-500">
              <Upload className="h-4 w-4 mr-1 text-gray-400" />
              DOCX
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-2">Maximum file size: 50MB</p>
        </div>
      )}
    </div>
  );
};
